import type { CardTag, Trip, Day, Place } from '@my-fullstack-app/itinerary-types';
import type { DesignDay, DesignPlace, DesignTrip } from './design-data';
import { resolvePlacePhotoUrl } from '../hooks/usePlacePhotoUrl';
import { unsplashUrl } from './unsplash';

/** Stable photo set for entities (trip, day, place) that have no own
 *  hero. Hashing the id picks a deterministic photo so the same entity
 *  always shows the same thumbnail across renders. Replace with real
 *  place-photo wiring when that lands later in the migration. */
// Two dead ids were removed from this set (photo-1502082553048… and
// photo-1601225998165…, both 404 at the Unsplash CDN — the latter flagged
// by Lighthouse as a console error). Kept to distinct ids so the hash
// rotation gives each entity deterministic photo variety.
const FALLBACK_HEROES = [
  unsplashUrl('photo-1488646953014-85cb44e25828', { w: 1000 }),
  unsplashUrl('photo-1531366936337-7c912a4589a7', { w: 1000 }),
  unsplashUrl('photo-1493780474015-ba834fd0ce2f', { w: 1000 }),
  unsplashUrl('photo-1483729558449-99ef09a8c325', { w: 1000 }),
  unsplashUrl('photo-1597212618440-806262de4f6b', { w: 1000 }),
  unsplashUrl('photo-1518803194621-27188ba362c9', { w: 1000 }),
  unsplashUrl('photo-1542051841857-5f90071e7989', { w: 1000 }),
];

const FALLBACK_AERIALS = [
  unsplashUrl('photo-1518803194621-27188ba362c9', { w: 1200 }),
  unsplashUrl('photo-1542051841857-5f90071e7989', { w: 1200 }),
  unsplashUrl('photo-1531366936337-7c912a4589a7', { w: 1200 }),
  unsplashUrl('photo-1539020140153-e479b8c22e70', { w: 1200 }),
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function heroFor(id: string): string {
  return FALLBACK_HEROES[hash(id) % FALLBACK_HEROES.length]!;
}

export function aerialFor(id: string): string {
  return FALLBACK_AERIALS[hash(id) % FALLBACK_AERIALS.length]!;
}

/** The Discover tab's mock map uses hardcoded pin percentages. Real
 *  places have lat/lng but the mock backdrop isn't projected to those
 *  coords — distribute pins on a grid as a placeholder until real maps
 *  land in a later phase. */
export function syntheticPin(index: number, total: number): { top: number; left: number } {
  if (total <= 1) return { top: 50, left: 50 };
  const cols = Math.ceil(Math.sqrt(total));
  const rows = Math.ceil(total / cols);
  const r = Math.floor(index / cols);
  const c = index % cols;
  return {
    top: 22 + (56 / Math.max(rows - 1, 1)) * r,
    left: 15 + (70 / Math.max(cols - 1, 1)) * c,
  };
}

export function adaptPlace(
  place: Place,
  index: number,
  totalInDay: number,
  notesMap: Record<string, string>,
  tagsMap: Record<string, CardTag> = {},
  photoWidth = 400,
): DesignPlace {
  return {
    id: place.id,
    // User-provided customLabel takes precedence over Google's name so the
    // rest of the UI doesn't need conditional logic for renamed places.
    name: place.customLabel ?? place.name,
    category: place.types?.[0]?.replace(/_/g, ' ') ?? 'Place',
    startTime: place.startTime ?? '',
    endTime: place.endTime ?? '',
    address: place.address ?? '',
    notes: notesMap[place.id],
    cardTag: tagsMap[place.id],
    hero: resolvePlacePhotoUrl(place, { width: photoWidth }) ?? heroFor(place.id),
    // Carry attribution through so TripReviewView's place tile can
    // render the photographer credit alongside the hero — Google Places
    // ToS §3.2.3 requires it on every photo surface.
    ...(place.photoAttribution ? { photoAttribution: place.photoAttribution } : null),
    pin: syntheticPin(index, totalInDay),
  };
}

export function adaptDay(
  day: Day,
  dayNumber: number,
  notesMap: Record<string, string>,
  tagsMap: Record<string, CardTag> = {},
  photoWidth = 400,
): DesignDay {
  return {
    id: day.id,
    label: day.label ?? `Day ${dayNumber}`,
    date: day.date,
    subtitle: day.label ?? '',
    hero: resolvePlacePhotoUrl(day.places[0] ?? null, { width: photoWidth }) ?? heroFor(day.id),
    places: day.places.map((p, i) => adaptPlace(p, i, day.places.length, notesMap, tagsMap, photoWidth)),
  };
}

/** Returns a full DesignTrip shape so the existing design-preview UI
 *  components consume it without refactoring. Fields that have no
 *  production analogue (rating, reviews, blurb, contributors,
 *  shareSlug) are stubbed with neutral defaults. */
/** Place photos available for a trip's hero gallery, in day-then-stop
 *  order. Each entry pairs the resolved photo URL with the place id, so
 *  the UI can lock a specific photo via Trip.heroPlaceId. */
export interface TripPhoto {
  placeId: string;
  url: string;
}

/**
 * Collect every place's photo URL for the trip's hero gallery.
 * Callers should pass the actual display width (× device DPR, capped
 * at 1600) so the right-sized image is fetched. The default of 1200
 * covers most non-thumbnail contexts without being excessively large.
 */
export function collectTripPhotos(trip: Trip, width = 1200): TripPhoto[] {
  const out: TripPhoto[] = [];
  for (const day of trip.days) {
    for (const place of day.places) {
      const url = resolvePlacePhotoUrl(place, { width });
      if (url) out.push({ placeId: place.id, url });
    }
  }
  return out;
}

/** Choose a stable photo for the trip cover. When `heroPlaceId` is set
 *  and corresponds to a place with a photo, that wins. Otherwise we
 *  pick a "random-feeling but stable" entry from the available photos
 *  by hashing the trip id — same trip always shows the same photo
 *  until the user swipes-and-locks something else. */
export function pickTripHero(trip: Trip, photos: TripPhoto[]): string {
  if (photos.length === 0) return heroFor(trip.id);
  if (trip.heroPlaceId) {
    const locked = photos.find((p) => p.placeId === trip.heroPlaceId);
    if (locked) return locked.url;
  }
  let h = 0;
  for (let i = 0; i < trip.id.length; i++) h = (h * 31 + trip.id.charCodeAt(i)) | 0;
  return photos[Math.abs(h) % photos.length]!.url;
}

export function adaptTrip(
  trip: Trip,
  notesMap: Record<string, string>,
  tagsMap: Record<string, CardTag> = {},
  photoWidth = 400,
): DesignTrip {
  const photos = collectTripPhotos(trip);
  return {
    id: trip.id,
    name: trip.name,
    destination: trip.destination,
    country: '',
    rating: 0,
    reviews: 0,
    startDate: trip.startDate,
    endDate: trip.endDate,
    duration: '',
    blurb: '',
    hero: pickTripHero(trip, photos),
    mapBackdrop: aerialFor(trip.id),
    days: trip.days.map((d, i) => adaptDay(d, i + 1, notesMap, tagsMap, photoWidth)),
    contributors: [],
    shareSlug: '',
  };
}
